'use client';

import { useState, useEffect, useRef } from 'react';
import { Series } from '@/lib/types';
import api from '@/lib/api';
import { useConfirm } from './ConfirmDialog';
import { useToast } from './Toast';
import { usePrivateMode } from '@/lib/privateMode';
import Link from 'next/link';

interface SeriesPanelProps {
  series: Series[];
  selectedSeriesId: number | null;
  totalImages: number;
  onSelectSeries: (seriesId: number | null) => void;
  onSeriesChange: () => void;
}

/** Opens the private prompt search already narrowed to one series. */
function SearchSeriesLink({ seriesId, seriesName }: { seriesId: number; seriesName: string }) {
  const privateEnabled = usePrivateMode((st) => st.enabled);
  if (!privateEnabled) return null;
  return (
    <Link
      href={`/search?series=${seriesId}`}
      onClick={(e) => e.stopPropagation()}
      title={`Search prompts within "${seriesName}"`}
      className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </Link>
  );
}

export default function SeriesPanel({
  series,
  selectedSeriesId,
  totalImages,
  onSelectSeries,
  onSeriesChange,
}: SeriesPanelProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState('');
  const [newSeriesPrivate, setNewSeriesPrivate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingPrivate, setEditingPrivate] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1); // -1 = none, 0 = All Images, 1+ = series
  const [isFocused, setIsFocused] = useState(false);
  const { confirm } = useConfirm();
  const toast = useToast();
  const panelRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Keyboard navigation
  useEffect(() => {
    if (!isFocused || isCreating || editingId !== null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in an input field
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const totalItems = series.length + 1; // +1 for "All Images"

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => Math.min(totalItems - 1, prev + 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex === 0) {
            onSelectSeries(null);
          } else if (focusedIndex > 0 && series[focusedIndex - 1]) {
            onSelectSeries(series[focusedIndex - 1].id);
          }
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (focusedIndex > 0 && series[focusedIndex - 1]) {
            handleDeleteSeries(series[focusedIndex - 1]);
          }
          break;
        case 'n':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setIsCreating(true);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setFocusedIndex(-1);
          panelRef.current?.blur();
          setIsFocused(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFocused, isCreating, editingId, series, focusedIndex, onSelectSeries]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  const handleCreateSeries = async () => {
    if (!newSeriesName.trim()) return;

    try {
      await api.createSeries({ name: newSeriesName.trim(), is_private: newSeriesPrivate });
      setNewSeriesName('');
      setNewSeriesPrivate(false);
      setIsCreating(false);
      onSeriesChange();
      toast.success(`Series "${newSeriesName.trim()}" created`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleDeleteSeries = async (s: Series) => {
    const confirmed = await confirm({
      title: 'Delete Series',
      message: `Delete "${s.name}" and all ${s.image_count} images? This cannot be undone.`,
      confirmText: 'Delete All',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await api.deleteSeries(s.id);
        if (selectedSeriesId === s.id) {
          onSelectSeries(null);
        }
        onSeriesChange();
        toast.success(`Deleted "${s.name}" and ${s.image_count} images`);
      } catch (err) {
        toast.error((err as Error).message);
      }
    }
  };

  const handleStartEdit = (s: Series) => {
    setEditingId(s.id);
    setEditingName(s.name);
    setEditingPrivate(s.is_private);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingName.trim()) return;

    const currentSeries = series.find(s => s.id === editingId);
    const updates: { name?: string; is_private?: boolean } = {};

    if (editingName.trim() !== currentSeries?.name) {
      updates.name = editingName.trim();
    }
    if (editingPrivate !== currentSeries?.is_private) {
      updates.is_private = editingPrivate;
    }

    if (Object.keys(updates).length === 0) {
      handleCancelEdit();
      return;
    }

    try {
      await api.updateSeries(editingId, updates);
      setEditingId(null);
      setEditingName('');
      setEditingPrivate(false);
      onSeriesChange();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingPrivate(false);
  };

  return (
    <div
      ref={panelRef}
      tabIndex={0}
      onFocus={() => { setIsFocused(true); if (focusedIndex === -1) setFocusedIndex(0); }}
      onBlur={() => setIsFocused(false)}
      className="h-full flex flex-col bg-[var(--bg-surface)] outline-none"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--border-subtle)]">
        <span className="text-sm font-medium text-[var(--text-secondary)]" title="↑↓ Navigate • Enter Select • Del Remove • N New">Series</span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* All Images option */}
        <button
          ref={el => { itemRefs.current[0] = el; }}
          onClick={() => { onSelectSeries(null); setFocusedIndex(0); }}
          className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-subtle cursor-pointer ${
            focusedIndex === 0 && isFocused
              ? 'ring-1 ring-[var(--accent)]'
              : ''
          } ${
            selectedSeriesId === null
              ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
              : 'hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">All Images</span>
            <span className="text-xs text-[var(--text-muted)]">{totalImages}</span>
          </div>
        </button>

        {/* Divider */}
        {series.length > 0 && (
          <div className="h-px bg-[var(--border-subtle)] my-2" />
        )}

        {/* Series list */}
        {series.map((s, index) => (
          <div
            key={s.id}
            ref={el => { itemRefs.current[index + 1] = el; }}
            onClick={() => setFocusedIndex(index + 1)}
            className={`group relative rounded-lg mb-1 transition-subtle ${
              focusedIndex === index + 1 && isFocused
                ? 'ring-1 ring-[var(--accent)]'
                : ''
            } ${
              selectedSeriesId === s.id
                ? 'bg-[var(--accent-soft)]'
                : 'hover:bg-[var(--bg-hover)]'
            }`}
          >
            {editingId === s.id ? (
              <div className="px-3 py-2">
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  className="input text-sm py-1"
                  autoFocus
                />
                <label className="flex items-center gap-2 mt-2 text-xs text-[var(--text-muted)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingPrivate}
                    onChange={(e) => setEditingPrivate(e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--border-default)] accent-[var(--accent)]"
                  />
                  Private (hidden from All Images)
                </label>
                <div className="flex gap-1 mt-2">
                  <button
                    onClick={handleSaveEdit}
                    className="btn btn-primary text-xs py-1 px-2"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-ghost text-xs py-1 px-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => onSelectSeries(s.id)}
                  className={`w-full text-left px-3 py-2.5 cursor-pointer ${
                    selectedSeriesId === s.id
                      ? 'text-[var(--accent)]'
                      : 'text-[var(--text-secondary)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate pr-8 flex items-center gap-1.5">
                      {s.name}
                      {s.is_private && (
                        <span title="Private">
                          <svg className="w-3.5 h-3.5 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                          </svg>
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <SearchSeriesLink seriesId={s.id} seriesName={s.name} />
                      <span className="text-xs text-[var(--text-muted)]">{s.image_count}</span>
                    </span>
                  </div>
                </button>

                {/* Edit/Delete buttons */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-40 group-hover:opacity-100 transition-opacity z-10 bg-[var(--bg-surface)] rounded-md p-0.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleStartEdit(s); }}
                    className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded transition-subtle cursor-pointer"
                    title="Rename"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteSeries(s); }}
                    className="p-1.5 text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--error-soft)] rounded transition-subtle cursor-pointer"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {/* Create new series */}
        {isCreating ? (
          <div className="px-3 py-2 mt-2">
            <input
              type="text"
              value={newSeriesName}
              onChange={(e) => setNewSeriesName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSeries();
                if (e.key === 'Escape') { setIsCreating(false); setNewSeriesName(''); setNewSeriesPrivate(false); }
              }}
              placeholder="Series name..."
              className="input text-sm py-1"
              autoFocus
            />
            <label className="flex items-center gap-2 mt-2 text-xs text-[var(--text-muted)] cursor-pointer">
              <input
                type="checkbox"
                checked={newSeriesPrivate}
                onChange={(e) => setNewSeriesPrivate(e.target.checked)}
                className="w-4 h-4 rounded border-[var(--border-default)] accent-[var(--accent)]"
              />
              Private (hidden from All Images)
            </label>
            <div className="flex gap-1 mt-2">
              <button
                onClick={handleCreateSeries}
                className="btn btn-primary text-xs py-1 px-2"
              >
                Create
              </button>
              <button
                onClick={() => { setIsCreating(false); setNewSeriesName(''); setNewSeriesPrivate(false); }}
                className="btn btn-ghost text-xs py-1 px-2"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsCreating(true)}
            className="w-full mt-2 px-3 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] rounded-lg transition-subtle cursor-pointer flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Series
          </button>
        )}
      </div>
    </div>
  );
}
